const DEFAULT_DELAY_MS = 7000;
const DEFAULT_STORAGE_KEY = 'oluvie:first-order-popup:v1';
const PROTECTED_SELECTOR = [
  '[data-or-primary]',
  '.or-product__purchase-save',
  '.or-product__purchase-share',
  '[data-or-share]',
  '.or-header__cart',
  '.or-product__navigation-cart',
  '[data-or-menu-toggle]',
  '[data-or-drawer-checkout]'
].join(',');

const EDITABLE_SELECTOR = 'input, select, textarea, [contenteditable="true"]';

const normalizeState = (value, delayMs) => {
  if (!value || typeof value !== 'object') return null;
  const elapsedMs = Number(value.elapsedMs);
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0 || typeof value.shown !== 'boolean') return null;
  return { elapsedMs: Math.min(delayMs, elapsedMs), shown: value.shown };
};

const overlaps = (a, b, padding = 8) => (
  a.width > 0 && a.height > 0 && b.width > 0 && b.height > 0 &&
  a.left < b.right + padding && a.right > b.left - padding &&
  a.top < b.bottom + padding && a.bottom > b.top - padding
);

export class FirstOrderPopupController {
  constructor({
    code,
    storage,
    resumeAfterSubmit = false,
    storageKey = DEFAULT_STORAGE_KEY,
    delayMs = DEFAULT_DELAY_MS,
    now = () => performance.now(),
    scheduler = { set: (callback, ms) => setTimeout(callback, ms), clear: (id) => clearTimeout(id) },
    isVisible = () => true,
    isSuppressed = () => false,
    view,
    copyText = async () => {},
    messages = { copied: 'Code gekopieerd.', copyFailed: 'Selecteer en kopieer de code.' }
  }) {
    this.code = String(code || '').trim();
    this.resumeAfterSubmit = Boolean(resumeAfterSubmit);
    this.storage = storage;
    this.storageKey = storageKey;
    this.delayMs = delayMs;
    this.now = now;
    this.scheduler = scheduler;
    this.isVisible = isVisible;
    this.isSuppressed = isSuppressed;
    this.view = view;
    this.copyText = copyText;
    this.messages = messages;
    this.state = { elapsedMs: 0, shown: false };
    this.startedAt = null;
    this.timer = null;
    this.started = false;
    this.failed = false;
  }

  verifyStorage() {
    if (!this.storage) return false;
    const probe = `${this.storageKey}:probe`;
    try {
      this.storage.setItem(probe, '1');
      this.storage.removeItem(probe);
      return true;
    } catch {
      return false;
    }
  }

  readState() {
    try {
      const raw = this.storage.getItem(this.storageKey);
      if (raw === null) return { elapsedMs: 0, shown: false };
      return normalizeState(JSON.parse(raw), this.delayMs);
    } catch {
      return null;
    }
  }

  writeState(nextState) {
    try {
      this.storage.setItem(this.storageKey, JSON.stringify(nextState));
      return true;
    } catch {
      return false;
    }
  }

  start() {
    if (this.started || !this.view || !this.verifyStorage()) return false;
    const stored = this.readState();
    if (!stored) return false;
    this.state = this.resumeAfterSubmit ? { elapsedMs: this.delayMs, shown: false } : stored;
    this.started = true;
    if (this.state.shown) return false;
    this.refresh();
    return true;
  }

  accumulate() {
    if (this.startedAt === null) return;
    this.state.elapsedMs = Math.min(this.delayMs, this.state.elapsedMs + Math.max(0, this.now() - this.startedAt));
    this.startedAt = null;
  }

  clearTimer() {
    if (this.timer !== null) this.scheduler.clear(this.timer);
    this.timer = null;
  }

  failClosed() {
    this.clearTimer();
    this.startedAt = null;
    this.failed = true;
    this.view.hide('storage-failure');
  }

  pause() {
    if (!this.started || this.failed || this.state.shown) return;
    this.accumulate();
    this.clearTimer();
    if (!this.writeState(this.state)) this.failClosed();
  }

  resume() {
    if (!this.started || this.failed || this.state.shown || this.startedAt !== null) return;
    if (this.state.elapsedMs >= this.delayMs) {
      this.attemptReveal();
      return;
    }
    this.startedAt = this.now();
    this.timer = this.scheduler.set(() => {
      this.timer = null;
      this.accumulate();
      if (!this.writeState(this.state)) {
        this.failClosed();
        return;
      }
      this.attemptReveal();
    }, this.delayMs - this.state.elapsedMs);
  }

  attemptReveal() {
    if (!this.started || this.failed || this.state.shown || !this.isVisible() || this.isSuppressed()) return false;
    this.clearTimer();
    this.accumulate();
    if (this.state.elapsedMs < this.delayMs) {
      this.resume();
      return false;
    }
    this.view.measure();
    const nextState = { elapsedMs: this.delayMs, shown: true };
    if (!this.writeState(nextState)) {
      this.failClosed();
      return false;
    }
    this.state = nextState;
    this.view.show();
    return true;
  }

  refresh() {
    if (!this.started || this.failed) return;
    if (this.state.shown) {
      if (this.view.isOpen() && this.isSuppressed()) this.close('protected-interaction');
      return;
    }
    if (!this.isVisible()) {
      this.pause();
      return;
    }
    if (this.state.elapsedMs >= this.delayMs) {
      this.attemptReveal();
      return;
    }
    this.resume();
  }

  close(reason = 'dismissed') {
    if (!this.view.isOpen()) return false;
    this.view.hide(reason);
    return true;
  }

  handleKey(key) {
    if (key !== 'Escape') return false;
    return this.close('escape');
  }

  handleProtectedInteraction() {
    return this.close('protected-interaction');
  }

  async copy() {
    if (!this.code || !this.state.shown || !this.view.isOpen()) return false;
    try {
      await this.copyText(this.code);
      this.view.announce(this.messages.copied, false);
      return true;
    } catch {
      this.view.announce(this.messages.copyFailed, true);
      return false;
    }
  }

  destroy() {
    if (!this.state.shown) this.pause();
    this.clearTimer();
    this.started = false;
    this.view.hide('destroyed');
  }
}

export const copyWithBrowser = async (text, win, doc) => {
  if (win.navigator?.clipboard?.writeText) {
    try {
      await win.navigator.clipboard.writeText(text);
      return;
    } catch {}
  }
  const active = doc.activeElement;
  const field = doc.createElement('textarea');
  field.value = text;
  field.readOnly = true;
  field.style.cssText = 'position:fixed;inset:auto auto 0 0;width:1px;height:1px;opacity:0;pointer-events:none';
  doc.body.append(field);
  field.select();
  const copied = doc.execCommand?.('copy');
  field.remove();
  if (active?.isConnected && typeof active.focus === 'function') active.focus({ preventScroll: true });
  if (!copied) throw new Error('copy-failed');
};

const createBrowserView = (root, doc) => {
  let focusBeforeOpen = null;
  return {
    measure() {
      focusBeforeOpen = doc.activeElement;
      root.hidden = false;
      root.dataset.state = 'measuring';
      root.setAttribute('aria-hidden', 'true');
    },
    collides() {
      if (root.hidden) return false;
      const popupRect = root.getBoundingClientRect();
      return [...doc.querySelectorAll(PROTECTED_SELECTOR)].some((node) => {
        if (root.contains(node) || node.closest('[hidden]')) return false;
        return overlaps(popupRect, node.getBoundingClientRect());
      });
    },
    show() {
      root.hidden = false;
      root.dataset.state = 'open';
      root.removeAttribute('aria-hidden');
    },
    hide(reason) {
      const restore = root.contains(doc.activeElement) ? focusBeforeOpen : null;
      root.dataset.state = 'closed';
      root.dataset.closeReason = reason;
      root.hidden = true;
      root.setAttribute('aria-hidden', 'true');
      if (restore?.isConnected && typeof restore.focus === 'function') restore.focus({ preventScroll: true });
    },
    isOpen() {
      return !root.hidden && root.dataset.state === 'open';
    },
    announce(message, error) {
      const status = root.querySelector('[data-or-promo-status]');
      if (!status) return;
      status.textContent = message;
      status.dataset.error = String(Boolean(error));
      status.setAttribute('aria-live', error ? 'assertive' : 'polite');
    }
  };
};

const pageIsSuppressed = (root, win, doc) => {
  const path = win.location?.pathname || '';
  if (/^\/(?:cart|checkouts?)(?:\/|$)/.test(path)) return true;
  if (doc.body?.matches('.or-page--cart, .or-menu-open')) return true;
  if (doc.querySelector('dialog[open]')) return true;
  const modal = doc.querySelector('[aria-modal="true"]:not([hidden])');
  if (modal && !root.contains(modal)) return true;
  const active = doc.activeElement;
  return Boolean(active && !root.contains(active) && active.matches?.(EDITABLE_SELECTOR));
};

const isProtectedTarget = (target) => Boolean(target?.closest?.(PROTECTED_SELECTOR));

export const handleProtectedEvent = (event, controller) => {
  if (!isProtectedTarget(event?.target)) return false;
  return controller.handleProtectedInteraction();
};

export const mountPopup = (root, { win = window, doc = document } = {}) => {
  if (!root || root.dataset.orPopupMounted === 'true') return null;
  root.dataset.orPopupMounted = 'true';
  let storage = null;
  try {
    storage = win.sessionStorage;
  } catch {
    root.hidden = true;
    return null;
  }
  const abort = new win.AbortController();
  const signal = abort.signal;
  const view = createBrowserView(root, doc);
  const controller = new FirstOrderPopupController({
    code: root.dataset.code,
    resumeAfterSubmit: root.dataset.signupResult === 'success' || root.dataset.signupResult === 'error',
    storage,
    storageKey: root.dataset.storageKey || DEFAULT_STORAGE_KEY,
    isVisible: () => doc.visibilityState === 'visible',
    isSuppressed: () => pageIsSuppressed(root, win, doc),
    view,
    copyText: (text) => copyWithBrowser(text, win, doc),
    messages: {
      copied: root.dataset.copySuccess || 'Code gekopieerd.',
      copyFailed: root.dataset.copyFailure || 'Selecteer en kopieer de code.'
    }
  });
  let frame = 0;
  const scheduleRefresh = () => {
    if (frame) return;
    frame = win.requestAnimationFrame(() => {
      frame = 0;
      controller.refresh();
    });
  };
  root.addEventListener('click', (event) => {
    if (event.target.closest('[data-or-promo-close]')) controller.close('dismissed');
    if (event.target.closest('[data-or-promo-copy]')) void controller.copy();
  }, { signal });
  doc.addEventListener('keydown', (event) => controller.handleKey(event.key), { signal });
  const protectedInteractionListener = (event) => handleProtectedEvent(event, controller);
  doc.addEventListener('pointerdown', protectedInteractionListener, { capture: true, signal });
  doc.addEventListener('click', protectedInteractionListener, { capture: true, signal });
  doc.addEventListener('submit', (event) => {
    if (event.target.matches?.('.or-product__form, form[action*="/cart/add"]')) controller.handleProtectedInteraction();
  }, { capture: true, signal });
  doc.addEventListener('oluvie:cart-added', () => controller.handleProtectedInteraction(), { signal });
  doc.addEventListener('visibilitychange', () => controller.refresh(), { signal });
  doc.addEventListener('focusin', scheduleRefresh, { signal });
  doc.addEventListener('focusout', scheduleRefresh, { signal });
  win.addEventListener('scroll', scheduleRefresh, { passive: true, signal });
  win.addEventListener('resize', scheduleRefresh, { passive: true, signal });
  win.addEventListener('pagehide', () => controller.pause(), { signal });
  win.addEventListener('pageshow', scheduleRefresh, { signal });
  const observer = new win.MutationObserver((records) => {
    if (records.some((record) => !root.contains(record.target))) scheduleRefresh();
  });
  observer.observe(doc.body, { subtree: true, attributes: true, attributeFilter: ['open', 'hidden', 'aria-expanded', 'class'] });
  const started = controller.start();
  if (!started) {
    root.hidden = true;
    observer.disconnect();
    abort.abort();
    root.dataset.orPopupMounted = 'false';
    return null;
  }
  return {
    controller,
    destroy() {
      if (frame) win.cancelAnimationFrame(frame);
      observer.disconnect();
      abort.abort();
      controller.destroy();
      root.dataset.orPopupMounted = 'false';
    }
  };
};

export const mountAllPopups = (doc = document) => [...doc.querySelectorAll('[data-or-first-order-popup]')]
  .map((root) => mountPopup(root, { doc, win: doc.defaultView || window }))
  .filter(Boolean);

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mountAllPopups(), { once: true });
  else mountAllPopups();
}

export { DEFAULT_DELAY_MS, DEFAULT_STORAGE_KEY, PROTECTED_SELECTOR, overlaps };
